import { createApi } from '@shared/redux/createApi';

const tags = {
  section: 'onboardingSection',
  pages: 'onboardingPages',
};

const normalizeSection = (s) => {
  if (!s || typeof s !== 'object') return s;
  return {
    id: s.section_id ?? s.id,
    title_ru: s.title?.ru ?? '',
    title_kg: s.title?.kg ?? '',
    description_ru: s.description?.ru ?? '',
    description_kg: s.description?.kg ?? '',
    icon: s.icon_url || null,
    is_active: !!s.is_active,
    sort_order: s.sort_order ?? 0,
    created: s.created,
    updated: s.updated,
  };
};

const normalizePage = (p) => {
  if (!p || typeof p !== 'object') return p;
  return {
    id: p.page_id ?? p.id,
    section_id: p.section_id,
    title_ru: p.title?.ru ?? '',
    title_kg: p.title?.kg ?? '',
    description_ru: p.description?.ru ?? '',
    description_kg: p.description?.kg ?? '',
    image: p.media_url || null,
    is_active: !!p.is_active,
    sort_order: p.sort_order ?? 0,
    created: p.created,
    updated: p.updated,
  };
};

const transformSectionsList = (response) => {
  // eslint-disable-next-line no-console
  console.log('[onboarding] getSections raw response:', response);
  const list = Array.isArray(response) ? response : (response?.items || response?.list || []);
  const normalized = list
    .map(normalizeSection)
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  // eslint-disable-next-line no-console
  console.log('[onboarding] getSections normalized:', normalized);
  return normalized;
};

const transformSection = (response) => {
  // eslint-disable-next-line no-console
  console.log('[onboarding] getSection raw response:', response);
  const normalized = normalizeSection(response);
  // eslint-disable-next-line no-console
  console.log('[onboarding] getSection normalized:', normalized);
  return normalized;
};

const transformPagesList = (response) => {
  // eslint-disable-next-line no-console
  console.log('[onboarding] getPages raw response:', response);
  const list = Array.isArray(response) ? response : (response?.items || response?.list || []);
  const normalized = list
    .map(normalizePage)
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  // eslint-disable-next-line no-console
  console.log('[onboarding] getPages normalized:', normalized);
  return normalized;
};

const buildSectionFormData = (data) => {
  const formData = new FormData();
  const textFields = ['title_ru', 'title_kg', 'description_ru', 'description_kg', 'sort_order', 'is_active'];
  for (const key of textFields) {
    if (data[key] !== undefined && data[key] !== null && data[key] !== '') {
      formData.append(key, data[key]);
    }
  }
  if (data.icon instanceof File) {
    formData.append('icon', data.icon, data.icon.name);
  }
  return formData;
};

const buildPageFormData = (data) => {
  const formData = new FormData();
  const fields = ['section_id', 'title_ru', 'title_kg', 'description_ru', 'description_kg', 'sort_order', 'is_active'];
  for (const key of fields) {
    if (data[key] !== undefined && data[key] !== null) {
      formData.append(key, data[key]);
    }
  }
  if (data.media instanceof File) {
    formData.append('media', data.media, data.media.name);
  }
  return formData;
};

export const onboardingApi = createApi({
  reducerPath: 'onboardingApi',
  tagTypes: Object.values(tags),
  endpoints: (builder) => ({
    getSections: builder.query({
      query: () => ({
        url: '/onboarding/admin/onboarding/sections',
        method: 'get',
      }),
      transformResponse: transformSectionsList,
      providesTags: [{ type: tags.section, id: 'LIST' }],
    }),

    getSection: builder.query({
      query: (id) => ({
        url: `/onboarding/admin/onboarding/sections/${id}`,
        method: 'get',
      }),
      transformResponse: transformSection,
      providesTags: (result, error, id) => [{ type: tags.section, id }],
    }),

    createSection: builder.mutation({
      query: ({ title_ru, title_kg, description_ru, description_kg, icon, sort_order = 0 }) => ({
        url: '/onboarding/admin/onboarding/sections',
        method: 'post',
        body: buildSectionFormData({ title_ru, title_kg, description_ru, description_kg, icon, sort_order }),
      }),
      invalidatesTags: [{ type: tags.section, id: 'LIST' }],
    }),

    updateSection: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/onboarding/admin/onboarding/sections/${id}`,
        method: 'patch',
        body: buildSectionFormData(patch),
      }),
      invalidatesTags: (result, error, arg) => [
        { type: tags.section, id: arg.id },
        { type: tags.section, id: 'LIST' },
      ],
    }),

    deleteSection: builder.mutation({
      query: (id) => ({
        url: `/onboarding/admin/onboarding/sections/${id}`,
        method: 'delete',
      }),
      invalidatesTags: [{ type: tags.section, id: 'LIST' }],
    }),

    getPages: builder.query({
      query: (sectionId) => ({
        url: `/onboarding/admin/onboarding/sections/${sectionId}/pages`,
        method: 'get',
      }),
      transformResponse: transformPagesList,
      providesTags: (result, error, sectionId) => [{ type: tags.pages, id: sectionId }],
    }),

    createPage: builder.mutation({
      query: ({ sectionId, title_ru, title_kg, description_ru, description_kg, sort_order = 0, media }) => ({
        url: `/onboarding/admin/onboarding/sections/${sectionId}/pages`,
        method: 'post',
        body: buildPageFormData({
          title_ru,
          title_kg,
          description_ru,
          description_kg,
          sort_order,
          media,
        }),
      }),
      invalidatesTags: (result, error, arg) => [
        { type: tags.pages, id: arg.sectionId },
        { type: tags.section, id: arg.sectionId },
      ],
    }),

    updatePage: builder.mutation({
      query: ({ id, sectionId, ...patch }) => ({
        url: `/onboarding/admin/onboarding/pages/${id}`,
        method: 'patch',
        body: buildPageFormData({ section_id: sectionId, ...patch }),
      }),
      invalidatesTags: (result, error, arg) => [
        { type: tags.pages, id: arg.sectionId },
      ],
    }),

    deletePage: builder.mutation({
      query: ({ id }) => ({
        url: `/onboarding/admin/onboarding/pages/${id}`,
        method: 'delete',
      }),
      invalidatesTags: (result, error, arg) => [
        { type: tags.pages, id: arg.sectionId },
      ],
    }),
  }),
});

export const {
  useGetSectionsQuery,
  useGetSectionQuery,
  useCreateSectionMutation,
  useUpdateSectionMutation,
  useDeleteSectionMutation,
  useGetPagesQuery,
  useCreatePageMutation,
  useUpdatePageMutation,
  useDeletePageMutation,
} = onboardingApi;
